import { Injectable } from '@angular/core';
import { LocalStorageService } from 'src/app/shared/services/local-storage.service';

export interface CompatibilityCheckResult {
    showBrowserWarning: boolean;
    showResolutionWarning: boolean;
    hasWarnings: boolean;
}

type NavigatorWithUserAgentData = Navigator & {
    userAgentData?: {
        brands?: { brand: string; version: string }[];
    };
};

export const compatibilityCheckPendingStorageKey: string =
    'compatibilityCheckPending';

@Injectable({
    providedIn: 'root',
})
export class BrowserCompatibilityCheckService {
    private static readonly MIN_WIDTH = 1920;
    private static readonly MIN_HEIGHT = 1080;

    constructor(private localStorageService: LocalStorageService) {}

    runChecks(): CompatibilityCheckResult {
        const showBrowserWarning = !this.isGoogleChrome();
        const showResolutionWarning = !this.hasMinResolution();

        return {
            showBrowserWarning,
            showResolutionWarning,
            hasWarnings: showBrowserWarning || showResolutionWarning,
        };
    }

    isGoogleChrome(): boolean {
        const userAgentData = (navigator as NavigatorWithUserAgentData)
            .userAgentData;
        const brands = userAgentData?.brands ?? [];

        if (brands.length > 0) {
            return brands.some((entry) => entry.brand === 'Google Chrome');
        }

        const userAgent = navigator.userAgent;
        const isChromeOrCriOS =
            userAgent.includes('Chrome/') || userAgent.includes('CriOS/');
        const isOtherChromiumBrowser =
            userAgent.includes('Edg/') ||
            userAgent.includes('OPR/') ||
            userAgent.includes('Brave/') ||
            userAgent.includes('SamsungBrowser/');

        return isChromeOrCriOS && !isOtherChromiumBrowser;
    }

    hasMinResolution(): boolean {
        return (
            window.screen.width >= BrowserCompatibilityCheckService.MIN_WIDTH &&
            window.screen.height >= BrowserCompatibilityCheckService.MIN_HEIGHT
        );
    }

    markCompatibilityCheckPending(): void {
        this.localStorageService.setItem(
            compatibilityCheckPendingStorageKey,
            ''
        );
    }

    isCompatibilityCheckPending(): boolean {
        return (
            this.localStorageService.getItem(
                compatibilityCheckPendingStorageKey
            ) !== null
        );
    }

    clearCompatibilityCheckPending(): void {
        this.localStorageService.removeItem(
            compatibilityCheckPendingStorageKey
        );
    }
}
